import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import TeacherAcademicHeader from "./TeacherAcademicHeader";
const MATERIAL_TYPES = ["Lecture", "Video", "PPT", "Word", "Sheet", "File"];

const ACCEPT_BY_TYPE = {
  Lecture: ".pdf,.ppt,.pptx,.doc,.docx,.mp4,.webm",
  Video: "video/*,.mp4,.webm,.mov,.avi,.mkv",
  PPT: ".ppt,.pptx",
  Word: ".doc,.docx",
  Sheet: ".xls,.xlsx,.csv,.pdf",
  File: "*/*",
};

function formatSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function TeacherMaterials({ courses = [], loading: parentLoading }) {
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const location = useLocation();

  const lockedCourse =
    location.state?.course ||
    courses.find((c) => String(c.id) === String(assignmentId)) ||
    null;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseId, setCourseId] = useState(
    lockedCourse ? String(lockedCourse.course_id || lockedCourse.id || "") : "",
  );
  const [classId, setClassId] = useState(
    lockedCourse ? String(lockedCourse.class_id || "") : "",
  );
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [materialType, setMaterialType] = useState("Lecture");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [listType, setListType] = useState("");
  const [reviewItem, setReviewItem] = useState(null);

  useEffect(() => {
    if (!lockedCourse) return;
    setCourseId(String(lockedCourse.course_id || lockedCourse.id || ""));
    setClassId(String(lockedCourse.class_id || ""));
  }, [lockedCourse]);

  const loadList = async () => {
    const params = new URLSearchParams();
    if (courseId) params.set("course_id", courseId);
    if (classId) params.set("class_id", classId);
    if (listType) params.set("material_type", listType);
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/materials?${params}`, {
      headers: getAuthHeaders(),
    });
    const data = res.ok ? await res.json() : [];
    setItems(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await loadList();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, classId, listType]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !courseId || !classId || !file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("course_id", courseId);
      fd.append("class_id", classId);
      fd.append("material_type", materialType);
      if (description.trim()) fd.append("description", description.trim());
      fd.append("file", file);

      const res = await fetch(`${API_BASE_URL}/api/teachers/me/materials`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      setTitle("");
      setDescription("");
      setFile(null);
      setMaterialType("Lecture");
      await loadList();
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    const res = await fetch(`${API_BASE_URL}/api/teachers/me/materials/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (res.ok) {
      if (reviewItem?.id === id) setReviewItem(null);
      loadList();
    }
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading materials…</p>
      </div>
    );
  }

  const fileUrl = reviewItem ? `${API_BASE_URL}${reviewItem.file_path}` : "";
  const isVideo =
    reviewItem &&
    (reviewItem.material_type === "Video" ||
      (reviewItem.mime_type || "").startsWith("video/"));

  return (
    <div className="tp-academic-shell">
      <TeacherAcademicHeader
        title="Lectures & Files"
        subtitle={
          lockedCourse
            ? `${lockedCourse.course_name || lockedCourse.name} · ${lockedCourse.class_name || "Class"}`
            : "Upload lectures, videos, PPT, Word docs, and worksheets."
        }
        backLabel="Back to course"
        onBack={
          assignmentId
            ? () => navigate(`/teacher/courses/${assignmentId}`, { state: { course: lockedCourse } })
            : null
        }
      />

      <div className="tp-academic-panel">
        <div className="tp-academic-panel__head">
          <h2>Upload material</h2>
          <p className="tp-muted">Video, PPT, Word, PDF, Excel, and more.</p>
        </div>
        <form onSubmit={submit} className="tp-form-grid tp-form-grid--academic">
          <label>
            Type
            <select value={materialType} onChange={(e) => setMaterialType(e.target.value)}>
              {MATERIAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>
            Title
            <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Material title" />
          </label>
          <label className="tp-form-full">
            Description
            <textarea
              className="tp-textarea tp-textarea--lg"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this lecture or file…"
            />
          </label>
          <label className="tp-form-full">
            File
            <input
              required
              type="file"
              accept={ACCEPT_BY_TYPE[materialType] || "*/*"}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <span className="tp-field-hint">Supports video, PPT/PPTX, Word, PDF, Excel, and other files.</span>
          </label>
          <div className="tp-form-full tp-form-actions">
            <button type="submit" className="tp-btn tp-btn-primary" disabled={saving}>
              {saving ? "Uploading…" : "Upload"}
            </button>
          </div>
        </form>
      </div>

      <div className="tp-academic-panel tp-academic-panel--secondary">
        <div className="tp-academic-panel__head">
          <h2>Uploaded files</h2>
          <div className="tp-academic-filters">
            <label>
              Filter
              <select value={listType} onChange={(e) => setListType(e.target.value)}>
                <option value="">All types</option>
                {MATERIAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="tp-muted">No materials uploaded yet.</p>
        ) : (
          <div className="tp-table-wrap tp-table-wrap--bordered">
            <table className="tp-table">              <thead>
                <tr>
                  <th>Type</th>
                  <th>Title</th>
                  <th>File</th>
                  <th>Size</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td><span className="tp-chip tp-chip--class">{m.material_type}</span></td>
                    <td>
                      <strong>{m.title}</strong>
                      {m.description && <div className="tp-muted">{m.description}</div>}
                    </td>
                    <td>{m.file_name}</td>
                    <td>{formatSize(m.file_size)}</td>
                    <td className="tp-table-actions">
                      <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => setReviewItem(m)}>
                        Review
                      </button>
                      <a className="tp-btn tp-btn--ghost tp-btn--sm" href={`${API_BASE_URL}${m.file_path}`} target="_blank" rel="noreferrer">
                        Open
                      </a>
                      <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => remove(m.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reviewItem && (
        <div className="tp-modal-overlay" onClick={() => setReviewItem(null)}>
          <div className="tp-modal tp-modal--wide" onClick={(e) => e.stopPropagation()}>
            <div className="tp-modal-head">
              <h3>{reviewItem.title}</h3>
              <button type="button" className="tp-btn tp-btn--ghost tp-btn--sm" onClick={() => setReviewItem(null)}>
                Close
              </button>
            </div>
            <p className="tp-muted">
              {reviewItem.material_type} · {reviewItem.file_name} · {formatSize(reviewItem.file_size)}
            </p>
            {reviewItem.description ? <p>{reviewItem.description}</p> : null}
            {isVideo ? (
              <video className="tp-material-preview" controls src={fileUrl}>
                Your browser does not support video playback.
              </video>
            ) : (
              <div className="tp-material-preview-fallback">
                <a href={fileUrl} target="_blank" rel="noreferrer" className="tp-btn tp-btn-primary">
                  Open / Download file
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TeacherMaterials;
